/**
 * Chiamate al task manager di un set (ADR 0029 §5.1), servito da `agents` sotto
 * `/api/v1/agent-sets/{set}`.
 *
 * Provvisorio dichiarato, come gli altri client: percorsi scritti a mano invece che
 * generati dagli schemi OpenAPI. `tasksApi.test.ts` presidia metodo, percorso e corpo.
 */

import type { ApiClient } from '../client';
import type {
  NewRelationKind,
  NewTask,
  Task,
  TaskActivity,
  TaskChanges,
  TaskComment,
  TaskDetail,
  TaskFilters,
  TaskMove,
  TaskPage,
  TaskState,
  TaskSummary,
} from './taskTypes';

/** Task massimi per pagina, come in `agents`. */
export const PAGE_SIZE = 50;

/**
 * Percorso dei task di un progetto del set.
 *
 * @param setId Set.
 * @param projectId Progetto del set.
 * @returns Il percorso.
 */
function projectTasks(setId: string, projectId: string): string {
  return `/api/v1/agent-sets/${encodeURIComponent(setId)}/projects/${encodeURIComponent(projectId)}/tasks`;
}

/**
 * Percorso di un task.
 *
 * @param setId Set.
 * @param projectId Progetto del set.
 * @param taskId Task.
 * @returns Il percorso.
 */
function taskPath(setId: string, projectId: string, taskId: string): string {
  return `${projectTasks(setId, projectId)}/${encodeURIComponent(taskId)}`;
}

/**
 * Query dei filtri di un elenco.
 *
 * @param filters Filtri.
 * @returns La query con il `?`, oppure vuota.
 */
export function query(filters: TaskFilters): string {
  const params = new URLSearchParams();
  for (const group of filters.groups ?? []) {
    params.append('group', group);
  }
  for (const kind of filters.kinds ?? []) {
    params.append('kind', kind);
  }
  for (const priority of filters.priorities ?? []) {
    params.append('priority', priority);
  }
  if (filters.assignee !== undefined) {
    params.set('assignee', filters.assignee);
  }
  if (filters.blocked !== undefined) {
    params.set('blocked', String(filters.blocked));
  }
  const text = filters.q?.trim() ?? '';
  if (text !== '') {
    params.set('q', text);
  }
  if (filters.roots === true) {
    params.set('roots', 'true');
  }
  if (filters.includeArchived === true) {
    params.set('include_archived', 'true');
  }
  if (filters.archivedOnly === true) {
    params.set('archived_only', 'true');
  }
  if (filters.updatedSince !== undefined) {
    params.set('updated_since', filters.updatedSince);
  }
  if (filters.sort !== undefined && filters.sort !== 'position') {
    params.set('sort', filters.sort);
  }
  if (filters.offset !== undefined && filters.offset > 0) {
    params.set('offset', String(filters.offset));
  }
  if (filters.limit !== undefined) {
    params.set('limit', String(Math.min(PAGE_SIZE, Math.max(1, filters.limit))));
  }
  const encoded = params.toString();
  return encoded === '' ? '' : `?${encoded}`;
}

/** Client del task manager. */
export class TasksApi {
  /**
   * @param client Client HTTP della sessione.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Stati del progetto del set (la prima lettura li crea).
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @returns Gli stati, in ordine di gruppo e posizione.
   */
  async listStates(setId: string, projectId: string): Promise<TaskState[]> {
    const page = await this.client.request<{ items: TaskState[] }>(
      'GET',
      `/api/v1/agent-sets/${encodeURIComponent(setId)}/projects/${encodeURIComponent(projectId)}/task-states`,
    );
    return page.items;
  }

  /**
   * Una pagina (al più 50) dei task di un progetto del set.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param filters Filtri, ordine e pagina.
   * @returns La pagina, con totale, conteggi per gruppo e istante della lettura.
   */
  listTasks(setId: string, projectId: string, filters: TaskFilters = {}): Promise<TaskPage> {
    return this.client.request<TaskPage>('GET', `${projectTasks(setId, projectId)}${query(filters)}`);
  }

  /**
   * Una pagina (al più 50) dei task di tutti i progetti del set: coda delle proposte,
   * scelta di una dipendenza in un altro progetto.
   *
   * @param setId Set.
   * @param filters Filtri, ordine e pagina.
   * @returns La pagina.
   */
  listSetTasks(setId: string, filters: TaskFilters = {}): Promise<TaskPage> {
    return this.client.request<TaskPage>(
      'GET',
      `/api/v1/agent-sets/${encodeURIComponent(setId)}/tasks${query(filters)}`,
    );
  }

  /**
   * Riepilogo del lavoro rimasto nel progetto.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @returns Il riepilogo.
   */
  summary(setId: string, projectId: string): Promise<TaskSummary> {
    return this.client.request<TaskSummary>('GET', `${projectTasks(setId, projectId)}/summary`);
  }

  /**
   * Un task con padre, sottotask e relazioni.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param taskId Task.
   * @returns Il dettaglio.
   */
  getTask(setId: string, projectId: string, taskId: string): Promise<TaskDetail> {
    return this.client.request<TaskDetail>('GET', taskPath(setId, projectId, taskId));
  }

  /**
   * Crea un task (o un sottotask, con `parent_id`).
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param task Dati.
   * @returns Il task.
   */
  createTask(setId: string, projectId: string, task: NewTask): Promise<TaskDetail> {
    return this.client.request<TaskDetail>('POST', projectTasks(setId, projectId), task);
  }

  /**
   * Modifica i campi indicati.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param taskId Task.
   * @param changes Campi.
   * @returns Il task.
   */
  updateTask(setId: string, projectId: string, taskId: string, changes: TaskChanges): Promise<Task> {
    return this.client.request<Task>('PATCH', taskPath(setId, projectId, taskId), changes);
  }

  /**
   * Cancella un task con i suoi sottotask.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param taskId Task.
   */
  async deleteTask(setId: string, projectId: string, taskId: string): Promise<void> {
    await this.client.request('DELETE', taskPath(setId, projectId, taskId));
  }

  /**
   * Sposta un task in uno stato o in un gruppo, alla posizione data.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param taskId Task.
   * @param move Arrivo e posizione.
   * @returns Il task.
   */
  moveTask(setId: string, projectId: string, taskId: string, move: TaskMove): Promise<Task> {
    return this.client.request<Task>('POST', `${taskPath(setId, projectId, taskId)}/transition`, {
      ...(move.stateId === undefined ? {} : { state_id: move.stateId }),
      ...(move.group === undefined ? {} : { group: move.group }),
      ...(move.position === undefined ? {} : { position: move.position }),
    });
  }

  /**
   * Approva una proposta.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param taskId Task proposto.
   * @returns Il task approvato.
   */
  approveTask(setId: string, projectId: string, taskId: string): Promise<Task> {
    return this.client.request<Task>('POST', `${taskPath(setId, projectId, taskId)}/approve`, {});
  }

  /**
   * Rifiuta una proposta.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param taskId Task proposto.
   * @param reason Motivo, salvato come commento se non è vuoto.
   * @returns Il task rifiutato.
   */
  rejectTask(setId: string, projectId: string, taskId: string, reason = ''): Promise<Task> {
    return this.client.request<Task>('POST', `${taskPath(setId, projectId, taskId)}/reject`, { reason });
  }

  /**
   * Commenti di un task.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param taskId Task.
   * @returns I commenti, dal più vecchio.
   */
  async listComments(setId: string, projectId: string, taskId: string): Promise<TaskComment[]> {
    const page = await this.client.request<{ items: TaskComment[] }>(
      'GET',
      `${taskPath(setId, projectId, taskId)}/comments`,
    );
    return page.items;
  }

  /**
   * Aggiunge un commento.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param taskId Task.
   * @param body Testo in Markdown.
   * @returns Il commento.
   */
  addComment(setId: string, projectId: string, taskId: string, body: string): Promise<TaskComment> {
    return this.client.request<TaskComment>('POST', `${taskPath(setId, projectId, taskId)}/comments`, {
      body,
    });
  }

  /**
   * Cronologia di un task.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param taskId Task.
   * @returns Le voci, dalla più vecchia.
   */
  async listActivity(setId: string, projectId: string, taskId: string): Promise<TaskActivity[]> {
    const page = await this.client.request<{ items: TaskActivity[] }>(
      'GET',
      `${taskPath(setId, projectId, taskId)}/activity`,
    );
    return page.items;
  }

  /**
   * Aggiunge una dipendenza o un collegamento.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param taskId Task che la dichiara.
   * @param kind Tipo.
   * @param relatedTaskId Altro task del set.
   * @returns Il task aggiornato con le relazioni.
   */
  addRelation(
    setId: string,
    projectId: string,
    taskId: string,
    kind: NewRelationKind,
    relatedTaskId: string,
  ): Promise<TaskDetail> {
    return this.client.request<TaskDetail>('POST', `${taskPath(setId, projectId, taskId)}/relations`, {
      kind,
      related_task_id: relatedTaskId,
    });
  }

  /**
   * Toglie una relazione.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param taskId Task.
   * @param relationId Relazione.
   * @returns Il task aggiornato.
   */
  removeRelation(setId: string, projectId: string, taskId: string, relationId: string): Promise<TaskDetail> {
    return this.client.request<TaskDetail>(
      'DELETE',
      `${taskPath(setId, projectId, taskId)}/relations/${encodeURIComponent(relationId)}`,
    );
  }
}
